import { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { useSelector } from "react-redux";
import { useQuery, useQueryClient } from "@tanstack/react-query";

// Hooks
import { useSubscriptions } from "@/Common/context/subscriptions/Subscriptions";
import { useFactoryWashout } from "@/Washouts/hooks/useFactoryWashout";

// API
import { getWashoutsSidePanelTree } from "@/Washouts/api/common/queries";

// Helpers
import { expandActiveCommissionCycle, refreshSidePanel } from "@/Washouts/helpers/sidePanelTrees";
import { addWashoutSubscriptions } from "@/Washouts/helpers/washoutSubscriptions";

// Components
import BodyLayer from "@/Layouts/components/Layers/BodyLayer";
import CollapsibleLeftPanel from "@/Layouts/components/CollapsibleLeftPanel";
import TechniciansWashoutContainer from "./TechniciansWashoutContainer";
import WashoutsSideBar from "@/Washouts/layouts/WashoutsSideBar";
import useCacheCleaner from "@/Common/hooks/CacheCleaner";

// Constants
const EMPLOYEE_ORDER_LABEL = "By Employee";
const QUERY_IDENTIFIERS = ["getWashout"];

const TechniciansWashoutLayer = (props) => {
	const { position = "", actions = {} } = props;
	const { clearCache } = useCacheCleaner();
	const positionWithoutSpaces = position.replace(/\s/g, "");
	const [selected, setSelected] = useState(null);
	const [expanded, setExpanded] = useState([]);
	const [toggleTitle, setToggleTitle] = useState(EMPLOYEE_ORDER_LABEL);
	const [open, setOpen] = useState(true);
	const [activeCommissionCycles, setActiveCommissionCycles] = useState([]);

	const { addSubscription, clearSubscriptionContext } = useSubscriptions();
	// Washout subscriptions
	const { useWashoutSubscriptions } = useFactoryWashout();
	const subscribeWashout = useWashoutSubscriptions(position);
	const onCreateWashout = subscribeWashout?.get("onCreateWashout");
	const onUpdateWashout = subscribeWashout?.get("onUpdateWashout");

	const { location, locationId } = useSelector((state) => state.location);
	const queryClient = useQueryClient();

	const subscriptionsInformation = {
		subscription: {
			context: `${positionWithoutSpaces}WashoutsCommissionCycles`,
			treeContext: `${positionWithoutSpaces}WashoutsTree`,
			hook: addSubscription,
		},
		onNewWashout: {
			context: `onNewTechniciansWashoutAvailable`,
			query: onCreateWashout?.query,
		},
		onUpdateWashout: {
			context: `onUpdateTechniciansWashout`,
			query: onUpdateWashout?.query,
		},
	};

	const { data: washoutsSidePanelTree, isLoading } = useQuery({
		queryKey: [`${positionWithoutSpaces}WashoutsTree`, location?.locationId],
		queryFn: async () => {
			const tree = await retrieveTechniciansWashoutsTreeData();
			if (tree) {
				const nodesToExpand = expandActiveCommissionCycle(tree.tree, tree.activeCommissionCycles, toggleTitle);
				setExpanded(nodesToExpand);
				setActiveCommissionCycles(tree.activeCommissionCycles);
				return tree;
			} else {
				return null;
			}
		},

		staleTime: Infinity,
		enabled: !!location?.locationId,
	});

	const retrieveTechniciansWashoutsTreeData = async () => {
		const tree = await getWashoutsSidePanelTree(location.locationId, position);
		if (tree) {
			const activeAndClosingCommissionCycles = await addWashoutSubscriptions(position, tree.tree, subscriptionsInformation);
			setActiveCommissionCycles(activeAndClosingCommissionCycles);
			return {
				tree: tree?.tree || [],
				activeCommissionCycles: activeAndClosingCommissionCycles,
			};
		} else {
			return false;
		}
	};

	const handleSelect = (currentSelected) => {
		if (currentSelected?._id && currentSelected._id !== selected?._id) {
			setOpen(!open);
			setSelected(currentSelected);
			setExpanded("");
		}
	};

	const handleOrderBy = (order) => {
		setToggleTitle(order);
		setExpanded([]);
	};

	const updateSidePanelCallBack = async (selectedNode) => {
		const washoutTreeCache = queryClient.getQueryData(
			[`${positionWithoutSpaces}WashoutsTree`, location?.locationId],
			(itemTree) => itemTree
		);
		const tree = washoutTreeCache?.tree;
		const newActiveCommissionCycles = washoutTreeCache?.activeCommissionCycles;

		refreshSidePanel(
			{
				tree,
				selectedNode,
				activeCommissionCycles: newActiveCommissionCycles,
			},
			{ toggleTitle, useRanking: true },
			{
				setExpanded,
				setSelected,
			}
		);
	};

	useEffect(() => {
		if (!location) {
			return null;
		}

		return () => {
			setSelected(null);
			setToggleTitle(EMPLOYEE_ORDER_LABEL);
			clearSubscriptionContext();
			clearCache(QUERY_IDENTIFIERS);
			setOpen(true);
		};
	}, [location, position, clearSubscriptionContext]);

	const isTreeLoading = isLoading || locationId !== location?.locationId;

	return (
		<BodyLayer
			floatingContent={
				<CollapsibleLeftPanel
					loading={isTreeLoading}
					open={true}
					permanentTrigger={open}
					control={
						<WashoutsSideBar
							treeDefinition={washoutsSidePanelTree}
							loading={isTreeLoading}
							selected={selected?._id ?? ""}
							toggleTitle={toggleTitle}
							onSelectAction={handleSelect}
							onOrderByAction={handleOrderBy}
						/>
					}
				/>
			}
		>
			<TechniciansWashoutContainer
				position={position}
				positionWithoutSpaces={positionWithoutSpaces}
				actions={actions}
				selected={selected}
				dataPresent={Boolean(selected)}
				location={location}
				updateSidePanelCallBack={updateSidePanelCallBack}
				activeCommissionCycles={activeCommissionCycles}
				subscriptionsInformation={subscriptionsInformation}
			/>
		</BodyLayer>
	);
};

TechniciansWashoutLayer.propTypes = {
	position: PropTypes.string,
	actions: PropTypes.object,
};

export default TechniciansWashoutLayer;
